import { isOrganizationReference } from '@traceio-org/platform-contracts';
import { appStateFor, sanitizeAppState, withoutAuthResponseParams } from './app-state.js';
import { decodeTokenPayload } from './token-claims.js';
import { ContextNotCarriedError, SessionExpiredError, SignInError, SignInRedirectError, } from './types.js';
import { throwIfAborted } from '../abort.js';
/** The hub's callback route. Every Trace app registers <origin>/auth/callback with the provider. */
export const AUTH_CALLBACK_PATH = '/auth/callback';
/**
 * Token errors that mean the provider session is gone. They end in the session-expired state, shown
 * once; they are never retried in a loop.
 */
const SESSION_ENDED_CODES = new Set([
    'login_required',
    'consent_required',
    'interaction_required',
    'invalid_grant',
    'missing_refresh_token',
]);
/** True when an access token was issued in this Auth0 organization (its org_id, or org_name). Not verified. */
function inOrganization(token, organization) {
    const payload = decodeTokenPayload(token);
    return payload?.org_id === organization || payload?.org_name === organization;
}
/** The current page as a return path (appStateFor drops it when parseReturnPath refuses it). */
function currentPage() {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}
/** The SDK's errors carry the OAuth error code in `error`; checking it avoids depending on class identity. */
function providerErrorCode(error) {
    if (typeof error !== 'object' || error === null)
        return undefined;
    const code = error.error;
    return typeof code === 'string' ? code : undefined;
}
function signInErrorCode(error, providerCode) {
    switch (providerCode) {
        case 'access_denied':
            return 'access_denied';
        case 'login_required':
        case 'consent_required':
        case 'interaction_required':
            return 'login_required';
        case 'missing_transaction':
        case 'state_mismatch':
        case 'invalid_state':
            return 'invalid_state';
        case 'request_error':
        case 'timeout':
            return 'network';
        case undefined:
            break;
        default:
            return 'unknown';
    }
    if (error instanceof TypeError)
        return 'network';
    // The SDK reports an ID token issued for another organization as a plain Error naming the claim.
    if (error instanceof Error && /\borg_(id|name)\b/.test(error.message))
        return 'organization_mismatch';
    return 'unknown';
}
/**
 * Trace session backed by Auth0 Universal Login through @auth0/auth0-spa-js. The SDK owns the OAuth
 * transaction (state, nonce, PKCE verifier); this class never reads or builds those values. Tokens stay
 * in the SDK's in-memory cache, so a page reload signs the hub out and the person signs in again through
 * the provider session.
 */
/** A portal context's id (contracts contextIds.portal: portal:<portalId>:<customerAccountId>). */
function isPortalContextId(contextId) {
    return contextId.startsWith('portal:');
}
export class Auth0Session {
    mode = 'auth0';
    redirectUri;
    config;
    clientPromise = null;
    completion = null;
    /** The Auth0 organization of each context this session was told about or signed in to. */
    organizations = new Map();
    /** The organization of the sign-in this page load completed: never redirected to again from this page. */
    completedOrganization = null;
    constructor(config) {
        this.config = config;
        this.redirectUri = `${config.origin ?? window.location.origin}${AUTH_CALLBACK_PATH}`;
    }
    client() {
        // The SDK is loaded on first use, so pages that never start or finish a sign-in do not download it.
        this.clientPromise ??= import('@auth0/auth0-spa-js').then(({ createAuth0Client }) => createAuth0Client({
            domain: this.config.domain,
            clientId: this.config.clientId,
            authorizationParams: { redirect_uri: this.redirectUri, audience: this.config.audience },
            // The hub is short-lived: memory only, so no token ever reaches localStorage.
            cacheLocation: 'memory',
            useRefreshTokens: true,
        }));
        // A failed creation must not poison every later call.
        this.clientPromise.catch(() => {
            this.clientPromise = null;
        });
        return this.clientPromise;
    }
    /**
     * Leaves for Universal Login. The returned promise only settles if the redirect fails, or when options.signal
     * aborted before the redirect started (then it rejects with the abort reason and nothing leaves).
     */
    async beginSignIn(options = {}) {
        const client = await this.client();
        // An abandoned attempt (DestinationGate's time limit or unmount) never leaves for the identity provider.
        throwIfAborted(options.signal);
        const authorizationParams = {};
        if (options.organization)
            authorizationParams.organization = options.organization;
        if (options.connection)
            authorizationParams.connection = options.connection;
        if (options.loginHint)
            authorizationParams.login_hint = options.loginHint;
        if (options.screenHint)
            authorizationParams.screen_hint = options.screenHint;
        if (options.prompt)
            authorizationParams.prompt = options.prompt;
        // Staff sign-ins (Trace Admin) and mfa_required reauthentications ask for MFA explicitly. The hub signs in
        // with its own Auth0 client, so the tenant's trace-mfa-policy Action cannot tell from the client alone that
        // the person is on the way to Trace Admin; without this, Admin answered mfa_required forever (dev, 2026-10-07).
        if (requiresMfa(options))
            authorizationParams.acr_values = MFA_ACR_VALUE;
        await client.loginWithRedirect({ authorizationParams, appState: appStateFor(options) });
    }
    /**
     * Finishes the redirect on /auth/callback and returns the validated app state. Code and state are
     * removed from the address bar whether it succeeds or fails, so a reload or a shared screenshot cannot
     * replay them.
     */
    completeSignIn(url = window.location.href) {
        // React StrictMode runs effects twice. The SDK deletes its transaction on the first call, so a second
        // call would fail with "Invalid state"; every caller shares the first completion instead.
        this.completion ??= this.runCompletion(url);
        return this.completion;
    }
    async runCompletion(url) {
        try {
            const client = await this.client();
            const result = await client.handleRedirectCallback(url);
            const state = sanitizeAppState(result.appState);
            // A sign-in for a workspace: the SDK checked that the ID token is in that organization, so the context
            // maps to it from now on, and this page never starts another sign-in there (getAccessToken).
            if (state.organization) {
                this.completedOrganization = state.organization;
                if (state.contextId)
                    this.organizations.set(state.contextId, state.organization);
            }
            return state;
        }
        catch (error) {
            const providerCode = providerErrorCode(error);
            const appState = sanitizeAppState(error?.appState);
            throw new SignInError(signInErrorCode(error, providerCode), appState, providerCode, { cause: error });
        }
        finally {
            window.history.replaceState(window.history.state, '', withoutAuthResponseParams(window.location.href));
        }
    }
    /**
     * A token for the audience through the SDK's cache and refresh tokens. Without options.contextId it is the
     * token of the current sign-in. With it, the token must name that context, and an Auth0 token names the
     * organization it was issued in (the claims Action adds its trace_org_id). Security relevant, so spelled out:
     * - The context maps to an Auth0 organization: options.organization (its organizationHint), remembered for
     *   the context, or one learned earlier (from such a call, or from the app state of a completed sign-in).
     *   Without one the context cannot be carried: ContextNotCarriedError, never a token of another context.
     * - The silent call asks in that organization ({ audience, organization }). The SDK caches one token per
     *   audience whatever its organization, and a refresh token stays in the organization it was issued in, so a
     *   token is used only when its org_id is that organization (one more call past the cache first).
     * - When no silent token is in it (an ended session, or only tokens of another organization), the person
     *   signs in to that organization through Universal Login (loginWithRedirect with the organization, the
     *   context in the app state) and this call rejects with SignInRedirectError while the page leaves. Never
     *   for the organization this page load just signed in to: that is a SessionExpiredError, so nothing loops.
     * - options.signal: once it aborted (the attempt was abandoned), the call rejects with its reason and never
     *   leaves for Universal Login. The SDK calls themselves cannot be cancelled, so it is checked after each.
     */
    async getAccessToken(audience = this.config.audience, options = {}) {
        const { signal } = options;
        const client = await this.client();
        throwIfAborted(signal);
        if (options.contextId === undefined)
            return this.silentToken(client, { audience });
        // A portal context has no Auth0 organization (the Customer Portal does not use them), so no token can name it:
        // the session's own token is the answer, and the product backend names the chosen context in its access check
        // (DestinationCheckAccess contextId), which platform-api answers only for a context the person holds. Before
        // this, every Auth0 portal sign-in ended on "We could not check your access" (dev, 2026-10-09).
        if (isPortalContextId(options.contextId))
            return this.silentToken(client, { audience });
        const organization = isOrganizationReference(options.organization)
            ? options.organization
            : this.organizations.get(options.contextId);
        if (!organization)
            throw new ContextNotCarriedError(options.contextId);
        this.organizations.set(options.contextId, organization);
        let token = null;
        let ended = null;
        try {
            token = await this.silentToken(client, { audience, organization });
            if (!inOrganization(token, organization))
                token = await this.silentToken(client, { audience, organization }, 'off');
            if (!inOrganization(token, organization))
                token = null;
        }
        catch (error) {
            if (!(error instanceof SessionExpiredError))
                throw error;
            ended = error;
        }
        if (token)
            return token;
        if (this.completedOrganization === organization) {
            throw new SessionExpiredError('organization_login_required', { cause: ended });
        }
        // Leaving for Universal Login is the side effect of this call: never for an attempt abandoned meanwhile.
        throwIfAborted(signal);
        await this.beginSignIn({
            contextId: options.contextId,
            organization,
            returnPath: currentPage(),
            ...(signal ? { signal } : {}),
        });
        throw new SignInRedirectError(organization);
    }
    async silentToken(client, authorizationParams, cacheMode) {
        let token;
        try {
            token = await client.getTokenSilently({ authorizationParams, ...(cacheMode ? { cacheMode } : {}) });
        }
        catch (error) {
            const code = providerErrorCode(error);
            if (code && SESSION_ENDED_CODES.has(code))
                throw new SessionExpiredError(code, { cause: error });
            throw error;
        }
        if (!token)
            throw new SessionExpiredError('no_token');
        return token;
    }
    async isAuthenticated() {
        const client = await this.client();
        return client.isAuthenticated();
    }
    /** Clears the SDK cache and leaves through the provider logout endpoint (Trace only, not Microsoft or Google). */
    async signOut(returnTo) {
        const client = await this.client();
        this.completion = null;
        await client.logout({ logoutParams: { returnTo } });
    }
}
/** OIDC PAPE multi-factor policy; the tenant's trace-mfa-policy Action enforces MFA when it is requested. */
export const MFA_ACR_VALUE = 'http://schemas.openid.net/pape/policies/2007/06/multi-factor';
function requiresMfa(options) {
    return (options.requireMfa === true || options.productKey === 'admin' || options.reauthenticate === 'mfa_required');
}
//# sourceMappingURL=auth0-session.js.map